import { handleRequest } from ".";
import { httpAuthorized } from "@/main";

/** 归档执行记录行（svc_archive_job_log 表；triggerType=RESTORE 为数据恢复操作） */
export interface ArchiveJobLog {
  id: string;
  triggerType: "SCHEDULED" | "MANUAL" | "RESTORE";
  targetMonth: string;
  startTime: string;
  endTime: string | null;
  totalSources: number;
  okSources: number;
  rowCount: number;
  fileBytes: number;
  status: "RUNNING" | "SUCCESS" | "PARTIAL" | "FAILED";
  errorSummary: string | null;
}

/** 归档登记行（svc_archive_registry 表，每源每月一行） */
export interface ArchiveRegistryRow {
  id: string;
  tableName: string;
  month: string;
  rowCount: number;
  filePath: string;
  fileSize: number;
  monthState: "EXPORTING" | "ALL_EXPORTED" | "ARCHIVED" | "FAILED" | "PARTIAL";
  sourceState: "PENDING" | "EXPORTED" | "ARCHIVED";
  exportTime: string;
  archiveTime: string | null;
}

/** 任务状态（含三个运行参数与最近执行记录） */
export interface ArchiveStatus {
  running: boolean;
  exportEnabled: boolean;
  deleteEnabled: boolean;
  retentionMonths: number;
  queryEnabled: boolean;
  recentJobs: ArchiveJobLog[];
}

/** 查询任务状态 + 最近 10 条执行记录 */
export const getArchiveStatusApi = async (): Promise<ArchiveStatus | null> => {
  return handleRequest<ArchiveStatus>(
    httpAuthorized.get("/archive/status"),
    (data) => data.data as ArchiveStatus
  );
};

/** 手动触发归档任务（异步启动，通过 status 轮询进度） */
export const runArchiveApi = async (): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post("/archive/run"),
    (data) => data.data.triggered === true
  );
};

/** 按月查登记明细（month 缺省 = 最近 6 个月全部） */
export const getArchiveRegistryApi = async (
  month?: string
): Promise<ArchiveRegistryRow[] | null> => {
  return handleRequest<ArchiveRegistryRow[]>(
    httpAuthorized.get("/archive/registry", {
      params: month ? { month } : {},
    }),
    (data) => data.data.rows ?? []
  );
};

/** 修改运行参数（key 白名单：export/delete/retention.months/query.enabled 四个 archive.* 参数） */
export const updateArchiveConfigApi = async (
  key: string,
  value: string
): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.put("/archive/config", { key, value }),
    (data) => data.data.updated === true
  );
};

/** 数据恢复运行状态（前端轮询；message=进行中/完成说明，error=失败原因） */
export interface RestoreState {
  running: boolean;
  tableName: string | null;
  month: string | null;
  restoredRows: number;
  message: string | null;
  error: string | null;
}

/** 可恢复清单（已删除 ARCHIVED 且有数据的源月，即 registry 行） */
export const getArchiveRestoreOptionsApi = async (): Promise<
  ArchiveRegistryRow[] | null
> => {
  return handleRequest<ArchiveRegistryRow[]>(
    httpAuthorized.get("/archive/restore/options"),
    (data) => data.data.rows ?? []
  );
};

/** 启动数据恢复（异步；归档任务/其他恢复运行中后端拒绝） */
export const startArchiveRestoreApi = async (
  tableName: string,
  month: string
): Promise<{ started: boolean; error?: string } | null> => {
  return handleRequest<{ started: boolean; error?: string }>(
    httpAuthorized.post("/archive/restore", { tableName, month }),
    (data) => data.data as { started: boolean; error?: string }
  );
};

/** 恢复运行状态轮询 */
export const getArchiveRestoreStatusApi =
  async (): Promise<RestoreState | null> => {
    return handleRequest<RestoreState>(
      httpAuthorized.get("/archive/restore/status"),
      (data) => (data.data.state as RestoreState) ?? null
    );
  };
